import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
/**
 * Supplier bulk-import wizard (BP-21/D-126). A modal: download the template or upload a CSV/Excel file,
 * see a parsed preview, confirm, then the server validates + skips VAT/LEI duplicates + inserts the rest
 * and returns a report. Excel (.xlsx) parsing loads SheetJS on demand from a CDN (no app dependency);
 * CSV is parsed natively. Tokens-only inline styling; the server is the source of truth (re-validates).
 */
const TEMPLATE_HEADERS = ['Name', 'Legal name', 'Country', 'VAT number', 'LEI', 'DUNS', 'Status'];
const FIELD_ALIASES = {
    name: 'name', supplier: 'name', 'supplier name': 'name', 'company': 'name', 'company name': 'name',
    'legal name': 'legalName', legalname: 'legalName', 'legal entity': 'legalName',
    country: 'country', 'country code': 'country',
    vat: 'vatNumber', 'vat number': 'vatNumber', vatnumber: 'vatNumber', 'vat no': 'vatNumber',
    lei: 'lei',
    duns: 'duns', 'duns number': 'duns',
    status: 'status',
};
// ── CSV parser (RFC-4180-ish: quotes, commas, CRLF) ──────────────────────────────────────────────
function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                }
                else
                    inQuotes = false;
            }
            else
                field += c;
        }
        else if (c === '"')
            inQuotes = true;
        else if (c === ',') {
            row.push(field);
            field = '';
        }
        else if (c === '\n') {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        }
        else if (c === '\r') { /* ignore */ }
        else
            field += c;
    }
    if (field !== '' || row.length > 0) {
        row.push(field);
        rows.push(row);
    }
    return rows.filter((r) => r.some((v) => v.trim() !== ''));
}
function rowsToObjects(table) {
    if (table.length < 2)
        return [];
    const headers = table[0].map((h) => FIELD_ALIASES[h.trim().toLowerCase()] ?? null);
    return table.slice(1).map((cells) => {
        const o = {};
        headers.forEach((field, i) => {
            if (!field)
                return;
            const v = (cells[i] ?? '').trim();
            if (v !== '')
                o[field] = v;
        });
        return o;
    });
}
// ── SheetJS on demand (no app dependency) ────────────────────────────────────────────────────────
let xlsxPromise = null;
function loadXlsx() {
    const w = window;
    if (w.XLSX)
        return Promise.resolve(w.XLSX);
    if (!xlsxPromise) {
        xlsxPromise = new Promise((resolve, reject) => {
            const sc = document.createElement('script');
            sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            sc.onload = () => resolve(window.XLSX);
            sc.onerror = () => reject(new Error('Could not load the Excel reader. Please check your connection, or upload a CSV.'));
            document.head.appendChild(sc);
        });
    }
    return xlsxPromise;
}
async function parseFile(file) {
    const isXlsx = /\.xlsx?$/i.test(file.name);
    if (!isXlsx)
        return rowsToObjects(parseCsv(await file.text()));
    const XLSX = await loadXlsx();
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const first = wb.Sheets[wb.SheetNames[0]];
    return rowsToObjects(parseCsv(XLSX.utils.sheet_to_csv(first)));
}
const label = { fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-soft)' };
export function SupplierImport({ a, onClose }) {
    const [rows, setRows] = useState(null);
    const [fileName, setFileName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);
    const namedRows = rows ? rows.filter((r) => (r.name ?? '').trim() !== '') : [];
    const missingName = rows ? rows.length - namedRows.length : 0;
    const onPick = (file) => {
        setError(null);
        setResult(null);
        setRows(null);
        setFileName(file.name);
        parseFile(file).then((parsed) => {
            if (parsed.length === 0)
                setError('No rows found. Check the file has a header row and at least one supplier.');
            setRows(parsed);
        }).catch((e) => setError(e.message || 'Could not read the file'));
    };
    const downloadTemplate = () => {
        const csv = TEMPLATE_HEADERS.join(',') + '\n' + 'Acme Components GmbH,Acme Components GmbH,DE,DE123456789,,,active\n';
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'supplier-import-template.csv';
        link.click();
        URL.revokeObjectURL(url);
    };
    const confirm = () => {
        if (!namedRows.length)
            return;
        setBusy(true);
        setError(null);
        a.importRows(namedRows)
            .then((res) => setResult(res))
            .catch((e) => setError(e.message || 'Import failed'))
            .finally(() => setBusy(false));
    };
    return (_jsx("div", { style: { position: 'fixed', inset: 0, background: 'rgba(22,36,29,.45)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 16 }, onClick: onClose, children: _jsxs("div", { style: { background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)', width: 'min(640px, 100%)', maxHeight: '90vh', overflow: 'auto', padding: '18px 20px' }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }, children: [_jsx("h2", { style: { fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600 }, children: "Import suppliers" }), _jsx("button", { onClick: onClose, style: { background: 'none', border: 'none', fontSize: 18, color: 'var(--ink-soft)', cursor: 'pointer' }, "aria-label": "Close", children: "\u2715" })] }), !result ? (_jsxs(_Fragment, { children: [_jsx("div", { style: { fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.5, marginBottom: 14 }, children: "Upload a CSV or Excel file. Use the template columns (Name is required). Duplicate VAT or LEI already in your register are skipped, never overwritten; nothing is saved until you confirm." }), _jsxs("div", { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }, children: [_jsx("button", { className: "btn btn-outline btn-sm", onClick: downloadTemplate, children: "Download template" }), _jsx("button", { className: "btn btn-outline btn-sm", onClick: () => inputRef.current?.click(), children: fileName ? 'Choose a different file' : 'Choose a file' }), _jsx("input", { ref: inputRef, type: "file", accept: ".csv,.xlsx,.xls", style: { display: 'none' }, onChange: (e) => { const f = e.target.files?.[0]; if (f)
                                        onPick(f); } }), fileName && _jsx("span", { style: { fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--ink-soft)', alignSelf: 'center' }, children: fileName })] }), error && _jsx("div", { style: { background: 'var(--red-tint)', border: '1px solid var(--line)', borderRadius: 10, padding: '9px 11px', fontSize: 12.5, color: 'var(--ink)', marginBottom: 12 }, children: error }), rows && rows.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex', gap: 18, marginBottom: 10, fontSize: 13 }, children: [_jsxs("span", { children: [_jsx("b", { children: namedRows.length }), " ready to import"] }), missingName > 0 && _jsxs("span", { style: { color: 'var(--amber)' }, children: [_jsx("b", { children: missingName }), " skipped (no name)"] })] }), _jsxs("div", { style: { border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }, children: [_jsxs("div", { style: { display: 'grid', gridTemplateColumns: '1.4fr 0.7fr 1fr 0.8fr', gap: 0, fontFamily: 'var(--font-mono)', fontSize: 11 }, children: [['Name', 'Country', 'VAT / LEI', 'Status'].map((h) => _jsx("div", { style: { ...label, padding: '7px 10px', background: 'var(--paper)' }, children: h }, h)), namedRows.slice(0, 8).map((r, i) => (_jsx(Frag, { cells: [r.name ?? '', r.country ?? '·', r.vatNumber || r.lei || '·', r.status ?? 'active'] }, i)))] }), namedRows.length > 8 && _jsxs("div", { style: { padding: '6px 10px', fontSize: 11.5, color: 'var(--ink-soft)', borderTop: '1px solid var(--line)' }, children: ["+ ", namedRows.length - 8, " more"] })] }), _jsxs("div", { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' }, children: [_jsx("button", { className: "btn btn-outline btn-sm", onClick: onClose, children: "Cancel" }), _jsx("button", { className: "btn btn-primary btn-sm", onClick: confirm, disabled: busy || !namedRows.length, children: busy ? 'Importing…' : `Import ${namedRows.length} supplier${namedRows.length === 1 ? '' : 's'}` })] })] }))] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { style: { background: 'var(--pine-tint)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }, children: [_jsxs("div", { style: { fontSize: 15, fontWeight: 600, color: 'var(--pine-deep)' }, children: ["Imported ", result.inserted, " supplier", result.inserted === 1 ? '' : 's'] }), _jsxs("div", { style: { fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 4 }, children: [result.skipped.length, " skipped (duplicate VAT/LEI) \u00B7 ", result.errors.length, " could not be read. They were not changed."] })] }), (result.skipped.length > 0 || result.errors.length > 0) && (_jsxs("div", { style: { maxHeight: 220, overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10, marginBottom: 12 }, children: [result.skipped.map((s, i) => _jsxs("div", { style: { display: 'flex', gap: 10, padding: '6px 10px', fontSize: 12, borderBottom: '1px solid var(--line)' }, children: [_jsxs("span", { style: { fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)' }, children: ["row ", s.row] }), _jsx("span", { children: s.name ?? '' }), _jsx("span", { style: { marginLeft: 'auto', color: 'var(--amber)' }, children: s.reason })] }, 's' + i)), result.errors.map((er, i) => _jsxs("div", { style: { display: 'flex', gap: 10, padding: '6px 10px', fontSize: 12, borderBottom: '1px solid var(--line)' }, children: [_jsxs("span", { style: { fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)' }, children: ["row ", er.row] }), _jsx("span", { style: { marginLeft: 'auto', color: 'var(--red)' }, children: er.reason })] }, 'e' + i))] })), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end' }, children: _jsx("button", { className: "btn btn-primary btn-sm", onClick: onClose, children: "Done" }) })] }))] }) }));
}
function Frag({ cells }) {
    return (_jsx(_Fragment, { children: cells.map((c, i) => _jsx("div", { style: { padding: '7px 10px', borderTop: '1px solid var(--line)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: c }, i)) }));
}
//# sourceMappingURL=SupplierImport.js.map